import React from 'react';
import { CreditCard, Shield, X } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded?: (method: Record<string, unknown>) => void;
}

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({ isOpen, onClose }) => {
  const { select } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <CreditCard className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {select({ es: 'Añadir método de pago', en: 'Add payment method' })}
              </h2>
              <p className="text-xs text-gray-500">
                {select({
                  es: 'Tus métodos de pago se gestionan de forma segura con Stripe.',
                  en: 'Your payment methods are managed securely with Stripe.',
                })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label={select({ es: 'Cerrar', en: 'Close' })}
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
              <Shield size={16} className="text-blue-600" />
              {select({ es: 'Pagos protegidos con Stripe', en: 'Payments protected with Stripe' })}
            </div>
            <p className="mt-1 text-xs text-blue-700">
              {select({
                es: 'SoArtistic nunca almacena los datos completos de tu tarjeta. Toda la información sensible se procesa directamente en Stripe.',
                en: 'SoArtistic never stores your full card details. All sensitive information is processed directly in Stripe.',
              })}
            </p>
          </div>

          <div className="text-center py-10 text-sm text-gray-600 space-y-4">
            <p>
              {select({
                es: 'Podrás ver aquí tus tarjetas guardadas y gestionarlas cuando estén asociadas a tu cuenta.',
                en: 'You will see your saved cards here and manage them when they are linked to your account.',
              })}
            </p>
            <p className="text-xs text-gray-400">
              {select({
                es: 'Esta sección se actualizará automáticamente cuando haya nuevos métodos o cambios de estado.',
                en: 'This section will update automatically when there are new methods or status changes.',
              })}
            </p>
          </div>

          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {select({ es: 'Entendido', en: 'Got it' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentMethodModal;
